import { ObserveRefreshData, Vehicle } from "../../../../../types/observe";
import {
  sendYmcCommand,
  setYmcStatusUpdateCallback,
  removeYmcStatusUpdateCallback,
} from "../../../../../utils/ymc-client";
import { useState, useEffect, useMemo } from "react";
import ConfirmModal from "../../../../../components/confirmModal/ConfirmModal";
import "./EiheijiRemoteControl.scss";

interface EiheijiRemoteControlProps {
  refreshData: ObserveRefreshData | null;
  vehicle: Vehicle | null;
  isDepartureAnnounceActive?: boolean;
}

interface YmcVehicleRawData {
  [key: string]: any;
}

const EiheijiRemoteControl = ({
  refreshData,
  vehicle,
  isDepartureAnnounceActive = false,
}: EiheijiRemoteControlProps) => {
  const [vehicleRawData, setVehicleRawData] = useState<YmcVehicleRawData>({});
  const [isStartConfirmOpen, setIsStartConfirmOpen] = useState(false);

  const isButtonEnabled =
    refreshData?.drive?.monitor_f === "1" && !refreshData?.other_selecting;

  const vehicleCode = vehicle?.vehicle_communication_code || "";

  // ボタン状態更新コールバック設定
  useEffect(() => {
    if (vehicleCode) {
      setYmcStatusUpdateCallback(vehicleCode, (ymcVehicleData: any) => {
        setVehicleRawData({
          ...ymcVehicleData,
        });
      });

      return () => {
        removeYmcStatusUpdateCallback(vehicleCode);
      };
    }
  }, [vehicleCode]);

  // 管制からの車両情報をボタンに状態反映する
  const buttonClasses = useMemo(() => {
    const currentMode = vehicleRawData.mode;
    const classes: { [key: string]: string } = {};

    const baseClasses = {
      changeAutoMode: "changeAutoModeBtn",
      changeRemoteMode: "changeRemoteModeBtn",
      serviceOut: "serviceOutBtn",
      startAutoCar: "startAutoCarBtn",
      stopAutoCar: "stopAutoCarBtn",
      recoveryAutoCar: "recoveryAutoCarBtn",
    };

    switch (currentMode) {
      case "out-of-service":
        classes.changeAutoMode =
          baseClasses.changeAutoMode + (!isButtonEnabled ? " disablebtn" : "");
        classes.changeRemoteMode =
          baseClasses.changeRemoteMode +
          (!isButtonEnabled ? " disablebtn" : "");
        classes.serviceOut = baseClasses.serviceOut + " active";
        classes.startAutoCar = baseClasses.startAutoCar + " disablebtn";
        classes.stopAutoCar = baseClasses.stopAutoCar + " disablebtn";
        classes.recoveryAutoCar = baseClasses.recoveryAutoCar + " disablebtn";
        break;

      case "auto-drive":
        classes.changeAutoMode = baseClasses.changeAutoMode + " active";
        classes.changeRemoteMode =
          baseClasses.changeRemoteMode +
          (!isButtonEnabled ? " disablebtn" : "");
        classes.serviceOut =
          baseClasses.serviceOut + (!isButtonEnabled ? " disablebtn" : "");
        classes.startAutoCar = baseClasses.startAutoCar + " notdisablebtn";
        classes.stopAutoCar = baseClasses.stopAutoCar + " notdisablebtn";
        classes.recoveryAutoCar =
          baseClasses.recoveryAutoCar + " notdisablebtn";
        break;

      case "remote-control":
        classes.changeAutoMode =
          baseClasses.changeAutoMode + (!isButtonEnabled ? " disablebtn" : "");
        classes.changeRemoteMode = baseClasses.changeRemoteMode + " active";
        classes.serviceOut =
          baseClasses.serviceOut + (!isButtonEnabled ? " disablebtn" : "");
        classes.startAutoCar = baseClasses.startAutoCar + " disablebtn";
        classes.stopAutoCar = baseClasses.stopAutoCar + " disablebtn";
        classes.recoveryAutoCar = baseClasses.recoveryAutoCar + " disablebtn";
        break;

      default: // 初期状態
        classes.changeAutoMode =
          baseClasses.changeAutoMode + (!isButtonEnabled ? " disablebtn" : "");
        classes.changeRemoteMode =
          baseClasses.changeRemoteMode +
          (!isButtonEnabled ? " disablebtn" : "");
        classes.serviceOut =
          baseClasses.serviceOut + (!isButtonEnabled ? " disablebtn" : "");
        classes.startAutoCar = baseClasses.startAutoCar + " disablebtn";
        classes.stopAutoCar = baseClasses.stopAutoCar + " disablebtn";
        classes.recoveryAutoCar = baseClasses.recoveryAutoCar + " disablebtn";
        break;
    }

    return classes;
  }, [vehicleRawData.mode, isButtonEnabled]);

  const getButtonClass = (buttonType: string): string => {
    let className = buttonClasses[buttonType] || "";
    if (buttonType === "startAutoCar" && isDepartureAnnounceActive) {
      className += " departureAnnounceActive";
    }
    return className;
  };

  // [自動運転]ボタンを押下（ボタン状態は管制からの戻りMode情報に従う）
  // 自動運転モードへの遷移を指示する
  const handleAutoModeClick = () => {
    if (!isButtonEnabled || !vehicle?.vehicles_vehicle_id) {
      console.log(`自動運転モード変更無効: ${vehicleCode}`);
      return;
    }
    sendYmcCommand("start-service-auto", vehicle.vehicles_vehicle_id);
  };

  // [遠隔運転]ボタンを押下（ボタン状態は管制からの戻りMode情報に従う）
  // 遠隔運転モードへの遷移を指示する
  const handleRemoteModeClick = () => {
    if (!isButtonEnabled || !vehicle?.vehicles_vehicle_id) {
      console.log(`遠隔運転モード変更無効: ${vehicleCode}`);
      return;
    }
    sendYmcCommand("start-service-remote", vehicle.vehicles_vehicle_id);
  };

  // [運用外]ボタンを押下（ボタン状態は管制からの戻りMode情報に従う）
  // 自動運転車両を運用外に切り替える
  const handleServiceOutClick = () => {
    if (!isButtonEnabled || !vehicle?.vehicles_vehicle_id) {
      console.log(`運用外切替無効: ${vehicleCode}`);
      return;
    }
    sendYmcCommand("out-of-service", vehicle.vehicles_vehicle_id);
  };

  // [発進]ボタンを押下
  // 自動運転車両に発進を指示する（確認ダイアログ表示）
  const handleStartClick = () => {
    if (!isButtonEnabled || !vehicle?.vehicles_vehicle_id) {
      console.log(`発進無効: ${vehicleCode}`);
      return;
    }
    setIsStartConfirmOpen(true);
  };

  // 発進確認ダイアログのOKボタン押下時
  const handleStartConfirm = () => {
    setIsStartConfirmOpen(false);
    if (!vehicle?.vehicles_vehicle_id) return;

    try {
      sendYmcCommand("start-driving", vehicle.vehicles_vehicle_id);
    } catch (error) {
      console.error(`YMC 発進指示失敗: ${vehicleCode}`, error);
    }
  };

  // [停止]ボタンを押下
  // 自動運転車両に円滑停止を指示する（現行の仕様。緊急停止指示だが準緊急をかけない）
  const handleStopClick = () => {
    if (!isButtonEnabled || !vehicle?.vehicles_vehicle_id) {
      console.log(`停止無効: ${vehicleCode}`);
      return;
    }

    try {
      sendYmcCommand("emergency-stop", vehicle.vehicles_vehicle_id);
    } catch (error) {
      console.error(`YMC 停止指示失敗: ${vehicleCode}`, error);
    }
  };

  // [復帰]ボタンを押下
  // 自動運転車両にエラー状態からの復帰を指示する
  const handleRecoveryClick = () => {
    if (!isButtonEnabled || !vehicle?.vehicles_vehicle_id) {
      console.log(`復帰無効: ${vehicleCode}`);
      return;
    }

    try {
      sendYmcCommand("recovery-vehicle", vehicle.vehicles_vehicle_id);
    } catch (error) {
      console.error(`YMC 復帰指示失敗: ${vehicleCode}`, error);
    }
  };

  return (
    <>
      <div className="eiheijiRemoteControlArea">
        <div className="title">車両管制制御</div>
        <div className="funcGroup_Border">
          <button
            className={getButtonClass("changeAutoMode")}
            onClick={handleAutoModeClick}
            id="changeAutoMode"
          >
            自動運転
          </button>
          <button
            className={getButtonClass("changeRemoteMode")}
            onClick={handleRemoteModeClick}
            id="changeRemoteMode"
          >
            遠隔運転
          </button>
          <button
            className={getButtonClass("serviceOut")}
            onClick={handleServiceOutClick}
            id="serviceOut"
          >
            運用外
          </button>
        </div>
        <div className="funcGroup">
          <button
            className={getButtonClass("startAutoCar")}
            onClick={handleStartClick}
            id="startAutoCar"
          >
            発進
          </button>
          <button
            className={getButtonClass("recoveryAutoCar")}
            onClick={handleRecoveryClick}
            id="recoveryAutoCar"
          >
            復帰
          </button>
          <button
            className={getButtonClass("stopAutoCar")}
            onClick={handleStopClick}
            id="stopAutoCar"
          >
            停止
          </button>
        </div>
      </div>

      <ConfirmModal
        isOpen={isStartConfirmOpen}
        icon="question"
        message={`${vehicle?.vehicles_vehicle_id}号車 発車しますか？`}
        onConfirm={handleStartConfirm}
        onCancel={() => setIsStartConfirmOpen(false)}
      />
    </>
  );
};

export default EiheijiRemoteControl;
